import React, { useEffect, useState } from "react";
import { api, formatGBP } from "@/lib/api";
import { Link } from "react-router-dom";
import { Ban } from "lucide-react";

export default function Pricing() {
  const [cats, setCats] = useState([]);
  const [charges, setCharges] = useState(null);
  const [notAccepted, setNotAccepted] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, o, n] = await Promise.all([
        api.get("/pricing/waste-categories"),
        api.get("/pricing/other-charges"),
        api.get("/pricing/not-accepted"),
      ]);
      setCats(c.data);
      setCharges(o.data);
      setNotAccepted(n.data.items || []);
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Pricing</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-white">
        Transparent. <span className="text-[#8DC63F]">By the kilo.</span>
      </h1>
      <p className="text-gray-400 mt-3 max-w-2xl">
        Final price is the actual weight we collect, charged at the per-kg rate of your waste type, with a small minimum to cover the trip.
      </p>

      <section className="mt-10" data-testid="pricing-waste-section">
        <h2 className="font-display text-2xl font-bold mb-4 text-white">Waste type · price per kg</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {cats.map((c) => (
            <div key={c.id} className="wa-card" data-testid={`pricing-cat-${c.id}`}>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-500">Per kg</div>
              <div className="font-display text-3xl font-black mt-1 text-[#8DC63F]">{formatGBP(c.sale_per_kg)}</div>
              <div className="text-sm text-gray-300 mt-1 font-semibold">{c.name}</div>
            </div>
          ))}
        </div>
      </section>

      {charges && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold mb-4 text-white">Service minimums</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile label="Self-load minimum" value={formatGBP(charges.self_load_min_charge)} />
            <Tile label="We-load minimum" value={formatGBP(charges.we_load_min_charge)} />
            <Tile label="We-load surcharge" value={`+${charges.we_load_surcharge_percent ?? 0}% on weight`} />
            <Tile label="Deposit" value={`${charges.deposit_percent}% (min ${formatGBP(charges.deposit_min)})`} />
          </div>
        </section>
      )}

      {notAccepted.length > 0 && (
        <section className="mt-12" data-testid="pricing-not-accepted">
          <h2 className="font-display text-2xl font-bold mb-4 text-white flex items-center gap-2">
            <Ban size={22} className="text-red-400" /> What we can&apos;t take
          </h2>
          <div className="wa-card border-red-500/30">
            <p className="text-sm text-gray-300">
              For safety, licensing or recycling-route reasons, we can&apos;t take any of the following.
              If you&apos;re unsure about a specific item, drop us a message before booking.
            </p>
            <ul className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5 text-sm text-gray-200 list-disc pl-5">
              {notAccepted.map((n) => (
                <li key={n} data-testid={`not-accepted-${n}`}>{n}</li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-[#8DC63F] font-semibold">
              Plasterboard must always be separated from all other waste.
            </p>
          </div>
        </section>
      )}

      <div className="mt-14 flex flex-wrap items-center gap-3">
        <Link to="/book" className="wa-btn-primary" data-testid="pricing-book-button">Book a collection</Link>
        <Link to="/skip-comparison" className="wa-btn-outline">See skip price comparison</Link>
      </div>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="wa-card">
      <div className="text-xs uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className="font-display text-2xl font-black mt-1 text-white">{value}</div>
    </div>
  );
}
